import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import AppHeader from '@/components/AppHeader';
import { getClubLogoUrl, uploadClubLogo, removeClubLogo } from '@/lib/supabase';

const DEFAULT_PLACEHOLDER_URL = 'https://images.pexels.com/photos/209861/pexels-photo-209861.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&dpr=1';

export default function ClubSettings({ onSignOut }: { onSignOut: () => void }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const displayUrl = logoUrl || DEFAULT_PLACEHOLDER_URL;

  const loadLogo = useCallback(async () => {
    try {
      const url = await getClubLogoUrl();
      setLogoUrl(url);
    } catch {
      setLogoUrl(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogo();
  }, [loadLogo]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('File size must be under 2MB.');
      return;
    }

    setError(null);
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);

    try {
      const url = await uploadClubLogo(file, logoUrl);
      setLogoUrl(url);
      setPreviewUrl(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!logoUrl) return;
    setRemoving(true);
    setError(null);
    try {
      await removeClubLogo(logoUrl);
      setLogoUrl(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove logo.');
    } finally {
      setRemoving(false);
    }
  };

  const handleReplaceClick = () => {
    fileRef.current?.click();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title="Club Settings"
        navIcon="back"
        onNavigate={() => navigate('/admin')}
      />

      <div className="flex-1 overflow-y-auto px-4 py-6 safe-area-bottom max-w-md mx-auto w-full">
        {/* Club Logo Section */}
        <div className="card mb-6">
          <h2 className="text-sm font-bold text-text-primary uppercase tracking-wider mb-4">Club Logo</h2>

          <div className="flex flex-col items-center gap-4 mb-4">
            {loading ? (
              <div className="w-24 h-24 rounded-full border-4 border-surface-raised border-t-team-a animate-spin" />
            ) : (
              <div className="relative">
                <img
                  src={previewUrl || displayUrl}
                  alt="Club Logo"
                  className="w-24 h-24 rounded-full object-cover shadow-lg ring-2 ring-surface-raised"
                />
                {uploading && (
                  <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                    <span className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            )}
            <div className="text-center">
              <p className="text-sm font-semibold text-text-primary">Starwood Sports Club</p>
              <p className="text-xs text-text-muted">{logoUrl ? 'Custom logo set' : 'Using default logo'}</p>
            </div>
          </div>

          {!isAdmin && (
            <p className="text-xs text-text-muted text-center">Only admins can change the club logo.</p>
          )}

          {isAdmin && (
            <div className="space-y-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              {logoUrl ? (
                <>
                  <button
                    onClick={handleReplaceClick}
                    disabled={uploading || removing}
                    className="btn-primary bg-team-a text-white font-bold disabled:opacity-50"
                  >
                    {uploading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        UPLOADING...
                      </span>
                    ) : (
                      'REPLACE LOGO'
                    )}
                  </button>
                  <button
                    onClick={handleRemove}
                    disabled={uploading || removing}
                    className="btn-secondary border-error text-error font-bold disabled:opacity-50"
                  >
                    {removing ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin" />
                        REMOVING...
                      </span>
                    ) : (
                      'REMOVE LOGO'
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={handleReplaceClick}
                  disabled={uploading}
                  className="btn-primary bg-team-a text-white font-bold disabled:opacity-50"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      UPLOADING...
                    </span>
                  ) : (
                    'UPLOAD LOGO'
                  )}
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 bg-error/20 border border-error rounded-lg p-3">
              <p className="text-error text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
